/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { isApproved } from './foreignApis';
import type Discussion from 'flarum/common/models/Discussion';

/** Why a review's export toggle is unavailable, in the order the tooltip reports them. */
export interface ReviewExportState {
	/** The review is already in the shop. */
	exported: boolean;
	/** The shop refuses further reviews for this article. */
	locked: boolean;
	/** The article is gone from the shop, or the review carries no article at all. */
	unavailable: boolean;
	/** flarum/approval is holding the discussion back. */
	notApproved: boolean;
	hidden: boolean;
	/** Any reason the toggle should be greyed out. `exported` is displayed, not blocked. */
	blocked: boolean;
	/** Exported *and* still counted as such — the shop can retract either of the two conditions. */
	countsAsExported: boolean;
	/** Nothing stands in the way of exporting this review now. */
	selectable: boolean;
}

/** Shared by `components/ReviewList` and `states/ReviewListState`. */
export default function reviewExportState(discussion: Discussion): ReviewExportState {
	const review = discussion.review();
	const article = review && review.article();

	const exported = !!(review && review.exported());
	const locked = !!(review && review.shopReviewsLocked());
	const unavailable = !(article && article.isAvailable());
	const notApproved = !isApproved(discussion);
	const hidden = discussion.isHidden();

	return {
		exported,
		locked,
		unavailable,
		notApproved,
		hidden,
		blocked: unavailable || locked || notApproved || hidden,
		// The exported flag is not cleared when the article disappears or reviews get locked.
		countsAsExported: exported && !unavailable && !locked,
		selectable: !exported && !unavailable && !locked && !notApproved && !hidden,
	};
}
